import * as Yup from 'yup'
import clsx from 'clsx'
import { Link, useNavigate } from 'react-router'
import { useFormik } from 'formik'
import { useSelector, useDispatch } from 'react-redux'
import { loginAndFetchClient, setCurrentClient } from '../../../redux/auth/authSlice';
import { set2FAOtpCodeResponse } from '../../../redux/auth/twoFactorAuthSlice';
import { AppDispatch, RootState } from '../../../redux/store';
import { useEffect, useRef, useState, useCallback } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { handleError } from '../../../utils/handleError'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLang } from '../../../../_metronic/i18n/Metronici18n'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'

const useLoginSchema = () => {
  const intl = useIntl();

  return Yup.object().shape({
    userName: Yup.string()
      .matches(/^(?:[a-zA-Z0-9_-]{1,50}|[^@\s]+@[^@\s]+\.[^@\s]+)$/, intl.formatMessage({ id: 'AUTH.VALIDATION.USER_NAME.INVALID' }))
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.USER_NAME.REQUIRED' })),
    password: Yup.string()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.PASSWORD.REQUIRED' })),
    rememberMe: Yup.boolean(),
  })
}

export function Login() {

  const currentLang = useLang();
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const authState = useSelector((state: RootState) => state.auth);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const resetRecaptcha = useCallback(() => {
    setCaptchaToken(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  }, []);

  const handleLogin = async (values: { userName: string; password: string; rememberMe: boolean; }) => {
    try {
      if (captchaRequired && !captchaToken) {
        formik.setStatus(intl.formatMessage({ id: 'AUTH.GENERAL.RECAPTCHA_COMPLETE' }));
        return;
      }

      const result = await dispatch(loginAndFetchClient({
        userName: values.userName,
        password: values.password,
        captchaToken: captchaRequired ? captchaToken : null,
      })).unwrap();
      if (result) {

        setCaptchaRequired(false);
        resetRecaptcha();

        if (values.rememberMe) {
          localStorage.setItem('rememberMe', values.rememberMe.toString());
          localStorage.setItem('USERNAME', values.userName);
        } else {
          localStorage.removeItem('rememberMe');
        }

        if (result.isTwoFactorActivated) {
          navigate('/auth/two-factor-auth');
        } else {
          dispatch(set2FAOtpCodeResponse(null));
          navigate('/dashboard');
        }
      }
    } catch (error) {

      formik.setStatus(handleError(error));
      if (error && typeof error === 'object' && 'captchaRequired' in error && error.captchaRequired === true) {
        setCaptchaRequired(true);
      }
      resetRecaptcha();
    }
  };

  const formik = useFormik({
    initialValues: {
      userName: localStorage.getItem('USERNAME') || '',
      password: '',
      rememberMe: localStorage.getItem('rememberMe')?.toLowerCase() === 'true',
    },
    validationSchema: useLoginSchema(),
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      try {
        if (captchaRequired && !captchaToken) {
          setStatus(intl.formatMessage({ id: 'AUTH.GENERAL.RECAPTCHA_COMPLETE' }));
          setSubmitting(false);
          return;
        }

        await handleLogin(values);
      } catch (error) {
        const errorMessage = handleError(error);
        setStatus(errorMessage);
        setSubmitting(false);
        dispatch(setCurrentClient(null));
        dispatch(set2FAOtpCodeResponse(null));
        resetRecaptcha();
      }
    },
  })

  const statusMessage = formik.status || (authState.sessionEndReason === 'service-unavailable'
    ? intl.formatMessage({ id: 'AUTH.GENERAL.SERVICE_UNAVAILABLE' })
    : null);

  useEffect(() => {
    setCaptchaRequired(false);
    resetRecaptcha();
  }, []);

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      {/* begin::Heading */}
      <div className='text-center mb-11'>
        {/* Logo */}
        <img
          src={toAbsoluteUrl('media/auth/img-sps-logo_2.png')}
          alt='Logo'
          style={{
            width: '50px',
            height: 'auto',
            marginBottom: '20px'
          }}
        />
        <h1 className='text-gray-900 fw-bolder mb-3'><FormattedMessage id="AUTH.LOGIN.TITLE" /></h1>
      </div>
      {/* begin::Heading */}

      {/* begin::Separator */}
      <div className='separator separator-content my-14'>
        <span className='w-125px text-gray-500 fw-semibold fs-7'></span>
      </div>
      {/* end::Separator */}

      {statusMessage ? (
        <div className={`mb-lg-15 alert ${authState.sessionEndReason === 'service-unavailable' && !formik.status ? 'alert-warning' : 'alert-danger'}`} role='alert'>
          <div className='alert-text font-weight-bold'>{statusMessage}</div>
        </div>
      ) : (<div></div>)}

      {/* begin::Form group */}
      <div className='fv-row mb-8 px-5'>
        <input
          placeholder={intl.formatMessage({ id: 'AUTH.GENERAL.USERNAME' })}
          {...formik.getFieldProps('userName')}
          className={clsx(
            'form-control bg-transparent text-uppercase',
            { 'is-invalid': formik.touched.userName && formik.errors.userName },
            {
              'is-valid': formik.touched.userName && !formik.errors.userName,
            }
          )}
          type='text'
          name='userName'
          autoComplete='username'
        />
        {formik.touched.userName && formik.errors.userName && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.userName}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-3 px-5'>
        <input
          placeholder={intl.formatMessage({ id: 'AUTH.LOGIN.PASSWORD' })}
          type='password'
          autoComplete='current-password'
          {...formik.getFieldProps('password')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.password && formik.errors.password,
            },
            {
              'is-valid': formik.touched.password && !formik.errors.password,
            }
          )}
        />
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-8 px-5'>
        <label className='form-check form-check-inline form-check-solid me-5 mt-5'>
          <input
            {...formik.getFieldProps('rememberMe')}
            className='form-check-input'
            type='checkbox'
            name='rememberMe'
            checked={formik.values.rememberMe}
          />
          {formik.touched.rememberMe && formik.errors.rememberMe && (
            <div className='fv-plugins-message-container'>
              <span role='alert'>{formik.errors.rememberMe}</span>
            </div>
          )}
          <span className='fw-bold ps-2 fs-7'><FormattedMessage id="AUTH.LOGIN.REMEMBER_ME" /></span>
        </label>
      </div>
      {/* end::Form group */}

      {captchaRequired && (
        <div className='fv-row mb-8 px-5'>
          <div className='text-center text-gray-600 fw-semibold fs-7 mb-4'>
            <FormattedMessage id='AUTH.LOGIN.SECURITY_CHECK' />
          </div>
          <div className='d-flex justify-content-center'>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={import.meta.env.VITE_TCGATE_RECAPTCHA_SITE_KEY}
              hl={currentLang}
              onChange={setCaptchaToken}
              onExpired={() => setCaptchaToken(null)}
              onErrored={() => setCaptchaToken(null)}
            />
          </div>
        </div>
      )}

      {/* begin::Wrapper */}
      <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8 px-5'>
        <div />

        {/* begin::Link */}
        <Link to='/auth/forgot-password' className='link-primary'>
          <FormattedMessage id="AUTH.GENERAL.FORGOT_BUTTON" />
        </Link>
        {/* end::Link */}
      </div>
      {/* end::Wrapper */}

      {/* begin::Action */}
      <div className='d-grid mb-10 px-5'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid || (captchaRequired && !captchaToken)}
        >
          {!authState.loading && <span className='indicator-label'><FormattedMessage id="AUTH.LOGIN.BUTTON" /></span>}
          {authState.loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              <FormattedMessage id="GENERAL.PLEASE_WAIT" />
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}

      {/* <div className='text-gray-500 text-center fw-semibold fs-6'>
        <FormattedMessage id="AUTH.LOGIN.CONTACT" />{' '}
        <Link to='mailto:' className='link-primary'>

        </Link>
      </div> */}
    </form>
  )
}
